import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  PutAdminV1StoresStoreIdVariantsPricingBulkBody,
  StoreVariantPricing,
} from '@nucleus-commerce/api-client/admin';

import { storeCatalogApi } from '../api/store-catalog-api';

import { storeCatalogKeys } from './store-catalog-keys';

/** Every variant of the store product with base, override and effective price; bounded, no cursor. */
export function storePricingQueryOptions(storeId: string, storeProductId: string) {
  return queryOptions({
    queryKey: storeCatalogKeys.pricing(storeId, storeProductId),
    queryFn: () => storeCatalogApi.pricing(storeId, storeProductId),
  });
}

export function useStorePricing(storeId: string, storeProductId: string) {
  return useQuery(storePricingQueryOptions(storeId, storeProductId));
}

function mergeRows(current: StoreVariantPricing[] | undefined, saved: StoreVariantPricing[]) {
  if (!current) return current;
  const byId = new Map(saved.map((row) => [row.variantId, row]));
  return current.map((row) => byId.get(row.variantId) ?? row);
}

export function useBulkVariantPricing(storeId: string, storeProductId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PutAdminV1StoresStoreIdVariantsPricingBulkBody) =>
      storeCatalogApi.bulkPricing(storeId, body),
    meta: { successMessage: 'storeCatalog:pricing.toasts.saved', silentError: true },
    onSuccess: (rows) => {
      queryClient.setQueryData<StoreVariantPricing[]>(
        storeCatalogKeys.pricing(storeId, storeProductId),
        (current) => mergeRows(current, rows),
      );
      // A read started before this save (a master save refreshes the rows) must not land after it.
      return queryClient.invalidateQueries({
        queryKey: storeCatalogKeys.pricing(storeId, storeProductId),
      });
    },
  });
}
